import { FC, ReactElement, useEffect, useState } from "react"
import { NewsService, News, UpdateNewsRequestSchema } from "../sdk"
import { Controller, useForm } from "react-hook-form"
import Input from "../components/Input"
import Button from "../components/Button"
import useError from "../hooks/useError"
import classNames from "classnames"
import { dateToInputDatetime } from "../helpers/date"

type Props = {
  defaultValues: News
  onSubmit?(): void
}

const Form: FC<Props> = ({
  defaultValues: {
    id: newsId,
    title: defaultTitle,
    description: defaultDescription,
    startPublicTimestamp: defaultStartPublicTimestamp,
    endPublicTimestamp: defaultEndPublicTimestamp
  },
  onSubmit: onSubmitProp
}) => {
  const { control, formState, handleSubmit, getValues, setValue } = useForm<UpdateNewsRequestSchema["updates"]>({
    defaultValues: {
      startPublicTimestamp: defaultStartPublicTimestamp
        ? dateToInputDatetime(new Date(defaultStartPublicTimestamp))
        : "",
      endPublicTimestamp: defaultEndPublicTimestamp
        ? dateToInputDatetime(new Date(defaultEndPublicTimestamp))
        : ""
    }
  })
  const { handleError } = useError()
  const [loading, setLoading] = useState<boolean>(false)

  const [startPublicDisabled, setStartPublicDisabled] = useState<boolean>(!defaultStartPublicTimestamp)
  const [endPublicDisabled, setEndPublicDisabled] = useState<boolean>(!defaultEndPublicTimestamp)

  const onSubmit = async({
    title,
    description,
    startPublicTimestamp,
    endPublicTimestamp
  }: UpdateNewsRequestSchema["updates"]): Promise<void> => {
    setLoading(true)
    try {
      await NewsService.updateNews({
        newsId,
        updates: {
          title,
          description,
          startPublicTimestamp: (!startPublicDisabled && startPublicTimestamp)
            ? new Date(startPublicTimestamp).toISOString()
            : null,
          endPublicTimestamp: (!endPublicDisabled && endPublicTimestamp)
            ? new Date(endPublicTimestamp).toISOString()
            : null
        }
      })
      if (onSubmitProp) {
        onSubmitProp()
      }
    } catch (err) {
      handleError(err)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!startPublicDisabled && !getValues("startPublicTimestamp")) {
      setValue("startPublicTimestamp", dateToInputDatetime(new Date()))
    }
  }, [startPublicDisabled])

  useEffect(() => {
    if (!endPublicDisabled && !getValues("endPublicTimestamp")) {
      setValue("endPublicTimestamp", dateToInputDatetime(new Date()))
    }
  }, [endPublicDisabled])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classNames(loading && "opacity-50 pointer-events-none")}>
      <Controller
        control={control}
        name="title"
        defaultValue={defaultTitle || ""}
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="text"
              label="Titolo"
              value={value}
              onChange={onChange}
              error={formState.errors.title?.message}
              className="mb-4"
            />
          )
        }}
      />

      <Controller
        control={control}
        name="description"
        defaultValue={defaultDescription || ""}
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="text"
              label="Descrizione"
              value={value}
              onChange={onChange}
              error={formState.errors.description?.message}
              className="my-4"
            />
          )
        }}
      />

      <div className="flex items-center w-56 justify-between">
        <label htmlFor="publicFromNow">Pubblica subito</label>
        <input
          id="publicFromNow"
          type="checkbox"
          checked={startPublicDisabled}
          onChange={(): void => setStartPublicDisabled(!startPublicDisabled)}
          className="ml-4"
        />
      </div>

      <div className="flex items-center w-56 justify-between">
        <label htmlFor="neverExpires">Nessuna data di scadenza</label>
        <input
          id="neverExpires"
          type="checkbox"
          checked={endPublicDisabled}
          onChange={(): void => setEndPublicDisabled(!endPublicDisabled)}
        />
      </div>

      <Controller
        control={control}
        name="startPublicTimestamp"
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="datetime-local"
              onChange={onChange}
              value={value!}
              label="Inizio pubblicazione"
              className="my-4"
              disabled={startPublicDisabled}
            />
          )
        }}
      />

      <Controller
        control={control}
        name="endPublicTimestamp"
        defaultValue={defaultEndPublicTimestamp || ""}
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="datetime-local"
              onChange={onChange}
              value={value!}
              label="Fine pubblicazione"
              className="mt-4"
              disabled={endPublicDisabled}
            />
          )
        }}
      />
      <Button type="submit" className="mt-6 w-full">Salva</Button>
    </form>
  )
}

export default Form
